import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";

// Champ libre avec suggestions plutôt qu'un choix fermé : la liste ne pourra jamais couvrir tous
// les corps de métier et toutes les formes de société réellement rencontrées, donc on suggère
// large sans jamais bloquer la saisie d'une valeur absente de la liste (voir le <input> plus bas).
// Groupé par famille (exporté) : réutilisé tel quel par le sous-filtre "secteur d'activité" de la
// carte Explorer, qui a besoin des familles pour proposer un choix compact plutôt que ~60 chips.
export const VENUE_TYPE_FAMILIES: { family: string; types: string[] }[] = [
  { family: "Loisirs & nightlife", types: [
    "Bar / Café", "Restaurant", "Boîte de nuit / Discothèque", "Bowling", "Casino",
    "Salle de spectacle", "Théâtre", "Cinéma", "Karting", "Escape game", "Laser game", "Salle de jeux / Arcade",
  ] },
  { family: "Sport & bien-être", types: [
    "Salle de sport", "Terrain de sport", "Piscine", "Patinoire", "Salle d'escalade", "Golf",
    "Spa / Institut de bien-être", "Salon de coiffure", "Institut de beauté",
  ] },
  { family: "Culture & savoir", types: [
    "Musée / Galerie", "Bibliothèque / Médiathèque", "Lieu de culte", "Salle polyvalente", "Studio (danse, musique, yoga...)",
  ] },
  { family: "Nature & extérieur", types: [
    "Parc / Espace vert", "Ferme / Domaine", "Camping",
  ] },
  { family: "Hébergement", types: [
    "Hôtel",
  ] },
  { family: "Commerce & artisanat", types: [
    "Commerce / Boutique", "Supermarché / Épicerie", "Centre commercial", "Marché", "Salon professionnel / Foire",
    "Concession automobile", "Garage automobile", "Carrossier", "Pharmacie", "Opticien", "Pressing", "Fleuriste", "Auto-école",
  ] },
  { family: "Corps de métier / BTP", types: [
    "Plombier", "Électricien", "Maçon / BTP", "Menuisier", "Peintre en bâtiment", "Chauffagiste", "Serrurier", "Paysagiste", "Couvreur",
  ] },
  { family: "Professions libérales & services", types: [
    "Cabinet médical", "Cabinet dentaire", "Cabinet de kinésithérapie", "Cabinet d'avocats", "Cabinet comptable",
    "Agence immobilière", "Agence de voyage", "Agence de communication", "Studio photo / vidéo",
    "Notaire", "Architecte", "Consultant / Freelance", "Traiteur", "Banque / Assurance", "Coworking",
  ] },
  { family: "Structures & organisations", types: [
    "Société", "Indépendant / Auto-entrepreneur", "Groupe", "Holding", "Franchise", "Startup",
    "Club / Fédération sportive", "ONG / Organisation caritative", "École / Organisme de formation", "Administration / Collectivité",
  ] },
  { family: "Autre", types: ["Autre"] },
];

const VENUE_TYPES = VENUE_TYPE_FAMILIES.flatMap(f => f.types);

// Suggestions dédiées quand subtype = "association" — VENUE_TYPES ci-dessus est taillé pour un
// commerce/une société (Bar, Plombier, Cabinet d'avocats...), rien de pertinent à suggérer à une
// association loi 1901. Catégories usuelles du monde associatif français plutôt que de faire
// deviner une correspondance dans la liste Pro.
const ASSOCIATION_TYPES = [
  "Sportive", "Culturelle", "Caritative / Humanitaire", "Étudiante", "Professionnelle / Syndicale",
  "Environnementale", "Religieuse / Cultuelle", "Loisirs / Passion", "Parents d'élèves",
  "Quartier / Résidents", "Santé / Entraide", "Jeunesse / Éducation populaire", "Autre",
];

// Sous-type de lieu — un axe différent de `type` (qui reste une simple catégorie descriptive).
// 'pro'/'association' débloquent le système de Membres (routes/venueMembers.ts côté backend) :
// un vrai lieu B2B veut contrôler qui rejoint, pas seulement compter des abonnés Follow.
const VENUE_SUBTYPES: { key: string; label: string; sub: string }[] = [
  { key: "lieu", label: "Lieu", sub: "Lieu OLTODO" },
  { key: "pro", label: "Pro", sub: "Indépendant, Société, Groupe" },
  { key: "association", label: "Association", sub: "" },
];
const JOIN_POLICIES: { key: string; label: string }[] = [
  { key: "approval", label: "Sur demande (vous validez chaque adhésion)" },
  { key: "open", label: "Ouvert (adhésion immédiate)" },
  { key: "invite_only", label: "Sur invitation uniquement" },
];

interface Props {
  onClose: () => void;
  onCreated?: (venue: any) => void;
  /** Pré-sélectionne l'onglet "Pro"/"Association" — utilisé par la landing /pro ("Créer ma page
   * gratuitement"), où le visiteur a déjà exprimé l'intention de créer un compte pro, pas un
   * simple lieu de sortie. */
  defaultSubtype?: string;
  /** Coche d'office "C'est mon établissement" — sans objet pour un lieu suggéré en passant
   * (VenueField), mais faux par défaut ailleurs serait absurde pour quelqu'un qui vient
   * explicitement de cliquer "Créer ma page". */
  forceOwner?: boolean;
}

export default function CreateVenueModal({ onClose, onCreated, defaultSubtype, forceOwner }: Props) {
  const navigate = useNavigate();
  const { lang } = useApp();
  const [name, setName] = useState("");
  const [subtype, setSubtype] = useState(defaultSubtype && VENUE_SUBTYPES.some(s => s.key === defaultSubtype) ? defaultSubtype : "lieu");
  const [joinPolicy, setJoinPolicy] = useState("approval");
  const [type, setType] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [isOwner, setIsOwner] = useState(!!forceOwner);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const typeList = subtype === "association" ? ASSOCIATION_TYPES : VENUE_TYPES;
  const typeSuggestions = type.trim()
    ? typeList.filter(v => v.toLowerCase().includes(type.trim().toLowerCase())).slice(0, 6)
    : typeList.slice(0, 6);

  const canSubmit = name && type && address && phone && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const geo = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(address)}&format=json&limit=1`).then(r => r.json()).catch(() => []);
      const latitude = geo?.[0]?.lat ? Number(geo[0].lat) : null;
      const longitude = geo?.[0]?.lon ? Number(geo[0].lon) : null;
      const data = await api.post("/venues", { name, type, address, phone: phone || null, website: website || null, is_owner: isOwner, latitude, longitude, subtype, join_policy: joinPolicy });
      setSuccess(true);
      if (data?.venue) onCreated?.(data.venue);
      // Sans redirection, un lieu tout juste créé n'a aucun lien visible nulle part dans l'app
      // (voir MyVenues.tsx : "Mes lieux" ne liste que les lieux suivis, pas les lieux possédés) —
      // on emmène directement vers la fiche, seul endroit où gérer photos/infos du lieu.
      setTimeout(() => {
        onClose();
        if (data?.venue?.id) navigate(`/venue/${data.venue.id}`);
      }, 1200);
    } catch {
      setSubmitting(false);
    }
  };

  // Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un position:fixed
  // descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:"touch") se positionne
  // sur Safari iOS par rapport au contenu défilant plutôt qu'au vrai viewport.
  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 17, fontWeight: 700 }}>{t(lang, "add_venue")}</span>
          <button onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
        </div>

        {success ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{t(lang, "venue_success")}</div>
          </div>
        ) : (
          <>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>{t(lang, "venue_name")} *</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Salle Pleyel, Le Five FC..." style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box" }} />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Type de lieu</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {VENUE_SUBTYPES.map(s => (
                <div
                  key={s.key}
                  onClick={() => { setSubtype(s.key); setType(""); }}
                  style={{ flex: 1, textAlign: "center", padding: "10px 6px", borderRadius: 10, cursor: "pointer", border: `1.5px solid ${subtype === s.key ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: subtype === s.key ? "#0a0a0a" : "white" }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: subtype === s.key ? "white" : "#0a0a0a" }}>{s.label}</div>
                  {!!s.sub && <div style={{ fontSize: 10, color: subtype === s.key ? "rgba(255,255,255,0.7)" : "#999", marginTop: 2 }}>{s.sub}</div>}
                </div>
              ))}
            </div>
            {subtype !== "lieu" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Qui peut rejoindre vos Membres ?</label>
                {JOIN_POLICIES.map(p => (
                  <div key={p.key} onClick={() => setJoinPolicy(p.key)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 4px", cursor: "pointer" }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", border: `1.5px solid ${joinPolicy === p.key ? "#0a0a0a" : "#CCC"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {joinPolicy === p.key && <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#0a0a0a" }} />}
                    </div>
                    <span style={{ fontSize: 13 }}>{p.label}</span>
                  </div>
                ))}
              </div>
            )}

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>{t(lang, "venue_type")} *</label>
            <div style={{ position: "relative", marginBottom: 12 }}>
              <input
                value={type}
                onChange={e => { setType(e.target.value); setShowTypeDropdown(true); }}
                onFocus={() => setShowTypeDropdown(true)}
                placeholder={subtype === "association" ? "Ex: Sportive, Culturelle, Caritative..." : "Ex: Bowling, Boîte de nuit, Plombier, Holding..."}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", boxSizing: "border-box" }}
              />
              {showTypeDropdown && (
                <>
                  <div onClick={() => setShowTypeDropdown(false)} style={{ position: "fixed", inset: 0, zIndex: 199 }} />
                  <div style={{ position: "absolute", top: "100%", left: 0, right: 0, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 10, zIndex: 200, boxShadow: "0 4px 20px rgba(0,0,0,0.1)", maxHeight: 260, overflowY: "auto" }}>
                    {typeSuggestions.map(v => (
                      <div key={v} onClick={() => { setType(v); setShowTypeDropdown(false); }} style={{ padding: "10px 14px", cursor: "pointer", fontSize: 14, backgroundColor: type === v ? "#F0F0F0" : "transparent" }}>{v}</div>
                    ))}
                    {typeSuggestions.length === 0 && (
                      <div style={{ padding: "10px 14px", fontSize: 12.5, color: "#999" }}>Aucune suggestion, votre saisie sera utilisée telle quelle.</div>
                    )}
                  </div>
                </>
              )}
            </div>

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>{t(lang, "venue_address")} *</label>
            <input value={address} onChange={e => setAddress(e.target.value)} placeholder="Ex: 12 rue des Sports, Paris 15" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box" }} />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>{t(lang, "venue_phone").replace(/\s*\(.*?\)/, "")} *</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+33 1 23 45 67 89" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box" }} />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>{t(lang, "venue_website")}</label>
            <input value={website} onChange={e => setWebsite(e.target.value)} placeholder="www.monlieu.fr" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 16, boxSizing: "border-box" }} />

            <div onClick={() => setIsOwner(!isOwner)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", backgroundColor: isOwner ? "#F0F0F0" : "#F8F8F8", borderRadius: 12, border: `1px solid ${isOwner ? "#0a0a0a" : "#E0E0E0"}`, cursor: "pointer", marginBottom: 16 }}>
              <div style={{ width: 44, height: 24, borderRadius: 12, backgroundColor: isOwner ? "#0a0a0a" : "#E0E0E0", position: "relative", flexShrink: 0 }}>
                <div style={{ position: "absolute", top: 2, left: isOwner ? 22 : 2, width: 20, height: 20, borderRadius: "50%", backgroundColor: "white", transition: "left 0.2s" }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{t(lang, "venue_owner")}</div>
                <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{t(lang, "venue_owner_sub")}</div>
              </div>
            </div>

            <button onClick={handleSubmit} disabled={!canSubmit} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: canSubmit ? "#0a0a0a" : "#E0E0E0", color: "white", fontSize: 14, fontWeight: 600, cursor: canSubmit ? "pointer" : "not-allowed" }}>
              {submitting ? "Envoi..." : t(lang, "venue_submit")}
            </button>
          </>
        )}
      </div>
    </div>
  ,
    document.body
  );
}
